import { supabase } from "@/integrations/supabase/client";

/* ============================================================
 * F30.2 — Global auth-failure recovery.
 *
 * Server functions guarded by `requireSupabaseAuth` throw
 * "Unauthorized" (401) or "Forbidden" (403). Left unhandled these
 * surface as a blank admin panel. This module classifies the error
 * and performs a single, consistent recovery path:
 *   clear auth → clear protected caches → redirect to /admin/login
 *   preserving the requested URL.
 * ============================================================ */

export type AuthFailureKind = "unauthorized" | "forbidden" | "offline" | "unknown";

const UNAUTH = /unauthorized|invalid token|jwt|not authenticated|401/i;
const FORBID = /forbidden|permission denied|not allowed|403/i;
const OFFLINE = /failed to fetch|networkerror|load failed|network request failed/i;

export function classifyError(error: unknown): AuthFailureKind {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : (() => {
            try {
              return JSON.stringify(error);
            } catch {
              return String(error);
            }
          })();

  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  if (UNAUTH.test(msg)) return "unauthorized";
  if (FORBID.test(msg)) return "forbidden";
  if (OFFLINE.test(msg)) return "offline";
  return "unknown";
}

export const AUTH_MESSAGE_FA: Record<AuthFailureKind, string> = {
  unauthorized: "نشست شما منقضی شده است. برای ادامه دوباره وارد شوید.",
  forbidden:
    "شما به این بخش دسترسی ندارید. اگر فکر می‌کنید اشتباهی رخ داده، با مدیر سیستم تماس بگیرید.",
  offline: "ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.",
  unknown: "خطای غیرمنتظره‌ای رخ داد. لطفاً دوباره تلاش کنید.",
};

let recovering = false;

/**
 * Handles an expired/invalid session: signs out, drops protected caches
 * and redirects to the login page with the current URL preserved.
 * Returns true when it took over the recovery.
 */
export async function recoverFromAuthFailure(
  error: unknown,
  opts: { queryClient?: { cancelQueries: () => Promise<void>; clear: () => void } } = {},
): Promise<boolean> {
  const kind = classifyError(error);
  if (kind !== "unauthorized") return false;
  if (recovering) return true;
  recovering = true;

  try {
    await opts.queryClient?.cancelQueries();
    opts.queryClient?.clear();
  } catch {
    /* non-fatal */
  }
  try {
    await supabase.auth.signOut();
  } catch {
    /* non-fatal */
  }

  if (typeof window !== "undefined") {
    const here = window.location.pathname + window.location.search;
    const dest = `/admin/login?redirect=${encodeURIComponent(here)}&reason=expired`;
    window.location.replace(dest);
  }
  return true;
}
